import { isTillOnlyRole, type PlatformRole } from "./roles";
import type { WorkspaceSession } from "./session";

/**
 * Client-side route *visibility* policy.
 *
 * This decides which screen the browser draws. It is not authorization: it runs
 * in code the viewer controls, on what the API said about the session, and the
 * API authorizes every request again (ADR 0023). Frontend visibility is not
 * authorization.
 *
 * The policy is written to fail closed. A route with no declared audience, an
 * unrecognised role, or no session at all is denied. There is no "allow" path
 * that does not name its roles explicitly.
 */

export type RouteDenialReason =
  /** Nobody is signed in in this browser. */
  | "no-session"
  /** Signed in, but holding no role in any shop. */
  | "no-shop"
  /** PRD section 4: cashiers work on the till and have no web workspace in v1. */
  | "till-only-role"
  /** The claimed role is not in the route's declared audience. */
  | "role-not-permitted"
  /** The route declared no audience. Fail closed rather than guess. */
  | "route-declares-no-audience";

export type RouteAccessDecision =
  | { granted: true }
  | { granted: false; reason: RouteDenialReason };

export type RouteAccessInput = {
  session: WorkspaceSession;
  /** Roles this route is drawn for. An empty or missing list denies everyone. */
  allowedRoles: readonly PlatformRole[] | undefined;
  /**
   * A permission the session's shop must also list, for a screen a Manager
   * sees only when an Admin has given them the right (P5-013 W6).
   */
  permission?: string;
};

export function resolveRouteAccess({ allowedRoles, permission, session }: RouteAccessInput): RouteAccessDecision {
  if (!allowedRoles || allowedRoles.length === 0) {
    return { granted: false, reason: "route-declares-no-audience" };
  }

  if (session.kind !== "signed-in") {
    return { granted: false, reason: "no-session" };
  }
  const role = session.role;
  if (role === null) {
    return { granted: false, reason: "no-shop" };
  }

  if (isTillOnlyRole(role)) {
    return { granted: false, reason: "till-only-role" };
  }

  if (!allowedRoles.includes(role)) {
    return { granted: false, reason: "role-not-permitted" };
  }
  if (permission !== undefined && !session.shop?.permissions.includes(permission)) {
    return { granted: false, reason: "role-not-permitted" };
  }

  return { granted: true };
}

/**
 * Plain, calm, actionable copy for each denial. No diagnostics, no hint about
 * which other role would have worked, and no invitation to retry a privileged
 * action.
 */
export const DENIAL_COPY: Record<RouteDenialReason, { title: string; description: string }> = {
  "no-session": {
    title: "Sign in to continue",
    description: "Sign in with your email, password and authenticator code to open the management workspace.",
  },
  "no-shop": {
    title: "Your account has no shop yet",
    description:
      "You are signed in, but your account has no role in any shop. Ask an Admin to give you access, then sign in again.",
  },
  "till-only-role": {
    title: "Cashiers work on the shop till",
    description:
      "Cashier accounts have no management workspace in BurgePOS v1. Sign in on the shop's Windows till to start a shift, sell and take payment.",
  },
  "role-not-permitted": {
    title: "This area is not part of your workspace",
    description:
      "Your account does not include this area. Ask an Admin if you need access, then use the navigation to return to a screen you work in.",
  },
  "route-declares-no-audience": {
    title: "This area is not available",
    description:
      "This screen has no audience configured, so BurgePOS will not open it. Report this address to support.",
  },
};
